import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaMenuItem from "../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSelectionIndicator from "../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaH7 from "../../ui-kit/typography/H7/H7";
import "./board-left-panel.scss";
import { activateOnKey } from "./a11y";
import { boardSlug, buildBoardPath } from "./board-routing";
import NewBoardModal from "./new-board-modal";
import { SavedView, SavedViews } from "./saved-views";
import { Board, Project, boardProjectKeys } from "./types";
import { useBoardConfig } from "./use-board";

interface BoardLeftPanelProps {
  // Read directly from the board config store for fresh updates; props kept for future embeds
}

interface BoardMenuItemProps {
  board: Board;
  projects: Project[];
  isActive: boolean;
  onClick: () => void;
}

const ProjectDot: React.FC<{ color: string }> = ({ color }) => (
  <span className="kuika-board-left-panel__dot" style={{ background: color }} aria-hidden="true" />
);

const MultiGlyph: React.FC = () => (
  <span className="kuika-board-left-panel__glyph kuika-board-left-panel__glyph--multi" aria-hidden="true">
    <span />
    <span />
    <span />
  </span>
);

const BoardMenuItem: React.FC<BoardMenuItemProps> = ({ board, projects, isActive, onClick }) => {
  const keys: string[] = boardProjectKeys(board);
  const leftIcon: React.ReactNode =
    board.scope.kind === "multi_project" ? (
      <MultiGlyph />
    ) : (
      <ProjectDot color={projects.find((entry: Project) => entry.key === keys[0])?.color ?? "var(--dark-4)"} />
    );

  return (
    <KuikaMenuItem
      data-testid={`board-menu-item-${boardSlug(board)}`}
      title={board.name}
      leftIcon={leftIcon}
      selected={isActive}
      onClick={onClick}
      rightUnit={
        board.scope.kind === "project" ? (
          <span className="kuika-board-left-panel__chip">{board.scope.projectKey}</span>
        ) : (
          <span className="kuika-board-left-panel__chip kuika-board-left-panel__chip--multi">{keys.length}</span>
        )
      }
    />
  );
};

const BoardLeftPanel: React.FC<BoardLeftPanelProps> = () => {
  const { config } = useBoardConfig();
  const navigate = useNavigate();
  const { domainName, boardSlug: urlBoardSlug } = useParams<{ domainName: string; boardSlug: string }>();
  const [newBoardOpen, setNewBoardOpen] = useState<boolean>(false);
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => SavedViews.list());

  useEffect(() => {
    const unsubscribe: () => void = SavedViews.subscribe((next: SavedView[]) => setSavedViews(next));
    void SavedViews.refresh();
    return unsubscribe;
  }, []);

  const projectBoards: Board[] = config.boards.filter((board: Board) => board.scope.kind === "project");
  const multiBoards: Board[] = config.boards.filter((board: Board) => board.scope.kind === "multi_project");

  const goTo = (board: Board): void => {
    navigate(buildBoardPath(domainName ?? "", board));
  };

  const activeSlug: string = (urlBoardSlug ?? "").toLowerCase();
  const isActive = (board: Board): boolean => boardSlug(board) === activeSlug;

  return (
    <div className="kuika-board-left-panel">
      <div className="kuika-board-left-panel__header">
        <KuikaH7>Boards</KuikaH7>
        {config.canManage && (
          <KuikaTooltip content="New board" position="right">
            <KuikaButton
              type="tertiary"
              size="sm"
              color="neutral"
              isIcon
              onClick={() => setNewBoardOpen(true)}
              data-testid="board-create-button"
            >
              <KuikaSvg name="plus" width={14} height={14} />
            </KuikaButton>
          </KuikaTooltip>
        )}
      </div>

      <div className="kuika-board-left-panel__scroll">
        <div className="kuika-board-left-panel__rows">
          <KuikaSelectionIndicator />
          {projectBoards.length > 0 && (
            <div className="kuika-board-left-panel__group">
              <div className="kuika-board-left-panel__group-title">Project boards</div>
              {projectBoards.map((board: Board) => (
                <BoardMenuItem
                  key={board.id}
                  board={board}
                  projects={config.projects}
                  isActive={isActive(board)}
                  onClick={() => goTo(board)}
                />
              ))}
            </div>
          )}

          {multiBoards.length > 0 && (
            <div className="kuika-board-left-panel__group">
              <div className="kuika-board-left-panel__group-title">Multi-project</div>
              {multiBoards.map((board: Board) => (
                <BoardMenuItem
                  key={board.id}
                  board={board}
                  projects={config.projects}
                  isActive={isActive(board)}
                  onClick={() => goTo(board)}
                />
              ))}
            </div>
          )}

          {savedViews.length > 0 && (
            <div className="kuika-board-left-panel__group">
              <div className="kuika-board-left-panel__group-title">Saved views</div>
              {savedViews.map((entry: SavedView) => (
                <div
                  key={entry.id}
                  className={classNames("kuika-board-left-panel__saved-row", OVERFLOW_REVEAL_HOST_CLASS)}
                >
                  <KuikaSvg name="star" width={12} height={12} />
                  <span
                    className="kuika-board-left-panel__saved-name"
                    onClick={() => navigate(entry.href)}
                    onKeyDown={activateOnKey(() => navigate(entry.href))}
                    role="link"
                    tabIndex={0}
                    title={entry.href}
                  >
                    <KuikaOverflowText>{entry.name}</KuikaOverflowText>
                  </span>
                  <button
                    type="button"
                    className="kuika-board-left-panel__saved-remove"
                    onClick={() => SavedViews.remove(entry.id)}
                    aria-label={`Remove ${entry.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {config.projects.length > 0 && (
            <div className="kuika-board-left-panel__group">
              <div className="kuika-board-left-panel__group-title">Projects</div>
              {config.projects.map((project: Project) => (
                <div
                  key={project.key}
                  className={classNames("kuika-board-left-panel__project-row")}
                  onClick={() => navigate(`/${domainName}/board/${project.key}`)}
                  onKeyDown={activateOnKey(() => navigate(`/${domainName}/board/${project.key}`))}
                  role="button"
                  tabIndex={0}
                  title={`Open ${project.name}`}
                >
                  <ProjectDot color={project.color} />
                  <span className="kuika-board-left-panel__project-name" title={project.name}>
                    {project.name}
                  </span>
                  <span className="kuika-board-left-panel__project-key">{project.key}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <NewBoardModal open={newBoardOpen} onClose={() => setNewBoardOpen(false)} />
    </div>
  );
};

export default BoardLeftPanel;
